/**
 * This service is a bridge between the application and whatever analytics
 * software is used.
 *
 * By defining the REACT_APP_ANALYTICS environment variable we are able to
 * either export the real or mocked implementation, so that from the application
 * point of view nothing changes and it can continue calling the analytics logic
 * as per usuall.
 */
import { merge } from 'lodash'
import { resourcesBrand } from '../../config'

const getModels = brand => {
  return import(`../../resources/${brand}/models`)
}

const getBrowserResolutionBreakpointString = width => {
  if (width <= 480) return 'xs'
  // eslint-disable-next-line no-else-return
  else if (width > 480 && width <= 720) return 's'
  // eslint-disable-next-line no-else-return
  else if (width > 720 && width <= 960) return 'm'
  // eslint-disable-next-line no-else-return
  else if (width > 960 && width <= 1280) return 'l'
  // eslint-disable-next-line no-else-return
  else if (width > 1280 && width <= 1600) return 'xl'
  // eslint-disable-next-line no-else-return
  else return 'xxl'
}

const mock = {
  ANALYTICS_DELAY: 1500,
  carModels: {},
  default: {},
  sleepDuration: 0,
  pageName: '',
  pageBottom: function pageBottom() {},

  setDefaults: function setDefaults(obj) {
    getModels(resourcesBrand).then(res => {
      this.carModels = res.carModels
    })
    merge(this.default, obj)
  },

  track: function track(obj) {
    const newStructure = {
      core: {
        pageInfo: {
          pageName:
            obj.pageName || window.du_digitalData?.core.pageInfo.pageName,
        },
        attributes: {
          viewChange:
            obj.viewChange || window.du_digitalData?.core.attributes.viewChange,
        },
      },
      event: obj.events,
      design: {
        browserResolutionBreakpoint: getBrowserResolutionBreakpointString(
          window.innerWidth
        ),
      },
      error: {
        errorCode: obj.errorCode ? obj.errorCode : null,
        errorMessage: obj.errorMessage ? obj.errorMessage : null,
        errorCausingURL: obj.errorCausingURL ? obj.errorCausingURL : null,
      },
      customerData: {
        loginStatus:
          obj.loginStatus || window.du_digitalData?.customerData.loginStatus,
      },
    }
    if (obj.faqText !== '') {
      newStructure.core.attributes.faqText = obj.faqText
    }
    const mergedData = merge({}, this.default, newStructure)

    window.du_digitalData = mergedData
  },

  trackError: function track(obj) {
    const newStructure = {
      core: {
        pageInfo: {
          pageName:
            obj.pageName || window.du_digitalData?.core.pageInfo.pageName,
        },

        attributes: {
          viewChange:
            obj.viewChange || window.du_digitalData?.core.attributes.viewChange,
        },
      },
      events: [
        {
          eventInfo: {
            eventType: obj.eventType,
            eventAction: obj.eventAction,
          },
        },
      ],
      error: {
        errorCode: obj.errorCode,
        errorMessage: obj.errorMessage,
      },
    }

    const mergedData = merge({}, this.default, newStructure)
    window.du_digitalData = mergedData
  },

  viewChange: function viewChange() {
    window._satellite && window._satellite.track && window._satellite.track('viewChange') // eslint-disable-line
  },

  page: function page() {
    if (!implementation.isLive) {
      // eslint-disable-next-line no-console
      console.log('track page', window.du_digitalData)
    }

    window._satellite && window._satellite.track && window._satellite.track('page') // eslint-disable-line
  },

  interaction: function interaction(sleepMs) {
    if (!implementation.isLive) {
      // eslint-disable-next-line no-console
      console.log('track interaction', window.du_digitalData)
    }

    window._satellite && window._satellite.track && window._satellite.track('interaction') // eslint-disable-line
    if (sleepMs) {
      sleep(sleepMs)
    }
  },

  download: function download() {
    if (!implementation.isLive) {
      // eslint-disable-next-line no-console
      console.log('track download', window.du_digitalData)
    }
    window._satellite && window._satellite.track && window._satellite.track('download') // eslint-disable-line
  },
}

const real = {
  ...mock,
  isLive: true,
  pageBottom: function pageBottom() {
    window._satellite && window._satellite.pageBottom() // eslint-disable-line
  },
}

let implementation = mock // eslint-disable-line

if (process.env.REACT_APP_ANALYTICS) {
  implementation = real
}

function sleep(ms) {
  const now = new Date().getTime()
  while (new Date().getTime() < now + ms) {
    return false
  }
  return true
}

export { implementation as Analytics }
